import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import {
  useCoachingCancellations,
  useConfirmCoachingCancellation,
  useCreateCoachingCancellationQuote,
  useReconcileCoachingCancellation,
} from "@bw/shared/hooks";
import type { CoachingCancellationQuote, CoachingLesson } from "@bw/shared/api";
import { coachingCancellationSummary, formatMoney } from "@bw/shared/utils";
import { coachingTime } from "./CoachingProposalPanel";

export function CoachingCancellationPanel({ acceptanceId, lesson, canAct }: {
  acceptanceId: string;
  lesson: CoachingLesson;
  canAct: () => boolean;
}) {
  const eligible = useMemo(() => lesson.schedule.kind === "fixed"
    ? lesson.sessions.filter((session) => session.status === "confirmed"
      && new Date(session.starts_at).getTime() > Date.now()) : [], [lesson]);
  const [selection, setSelection] = useState<"one_session" | "remaining_series">("one_session");
  const [sessionKey, setSessionKey] = useState(eligible[0]?.session_key ?? "");
  const [quote, setQuote] = useState<CoachingCancellationQuote | null>(null);
  const [quoteExpired, setQuoteExpired] = useState(false);
  const [historyRequested, setHistoryRequested] = useState(false);
  const quoteKeys = useRef(new Map<string, string>()), confirmKeys = useRef(new Map<string, string>());
  const createQuote = useCreateCoachingCancellationQuote();
  const confirm = useConfirmCoachingCancellation();
  const recover = useReconcileCoachingCancellation();
  const history = useCoachingCancellations(acceptanceId,
    lesson.fulfillment.state === "funded" && historyRequested);
  const signature = `${selection}:${selection === "one_session" ? sessionKey : "remaining"}`;
  const busy = createQuote.isPending || confirm.isPending || recover.isPending;
  const eligibleSignature = eligible.map((item) => item.session_key).join(":");
  useEffect(() => {
    if (!eligible.some((item) => item.session_key === sessionKey)) {
      setSessionKey(eligible[0]?.session_key ?? "");
    }
    setQuote(null); setQuoteExpired(false); quoteKeys.current.clear(); confirmKeys.current.clear();
  }, [acceptanceId, eligibleSignature]); // authoritative session inventory changed
  useEffect(() => {
    if (!quote) { setQuoteExpired(false); return; }
    const delay = new Date(quote.expires_at).getTime() - Date.now();
    if (delay <= 0) { setQuoteExpired(true); return; }
    const timer = window.setTimeout(() => setQuoteExpired(true), delay);
    return () => window.clearTimeout(timer);
  }, [quote]);
  const discardQuote = () => {
    quoteKeys.current.delete(signature);
    if (quote) confirmKeys.current.delete(quote.quote_id);
    setQuote(null); setQuoteExpired(false);
  };
  if (lesson.fulfillment.state !== "funded" || lesson.schedule.kind !== "fixed") return null;
  return <Paper component="section" aria-label="Cancel coaching sessions" variant="outlined" sx={{ p: 2.5, mt: 1 }}>
    <Stack spacing={1.5}>
      <Stack><Typography component="h3" sx={{ fontWeight: 700 }}>Need to cancel?</Typography>
        <Typography variant="body2" color="text.secondary">Review the accepted policy and exact refund before anything changes.</Typography></Stack>
      {eligible.length ? <>
        <TextField select size="small" label="What should be cancelled?" value={selection}
          disabled={busy || Boolean(quote)} onChange={(event) => { setSelection(event.target.value as typeof selection); setQuote(null); }}>
          <MenuItem value="one_session">One session</MenuItem>
          <MenuItem value="remaining_series">All remaining sessions</MenuItem>
        </TextField>
        {selection === "one_session" ? <TextField select size="small" label="Session" value={sessionKey}
          disabled={busy || Boolean(quote)} onChange={(event) => { setSessionKey(event.target.value); setQuote(null); }}>
          {eligible.map((session) => <MenuItem key={session.session_key} value={session.session_key}>{coachingTime(session.starts_at, lesson.schedule.timezone)}</MenuItem>)}
        </TextField> : null}
        {!quote ? <Button variant="outlined" disabled={busy || !sessionKey || !canAct()} onClick={async () => {
          if (!canAct()) return;
          if (!quoteKeys.current.has(signature)) quoteKeys.current.set(signature, crypto.randomUUID());
          try { setQuote(await createQuote.mutateAsync([acceptanceId, { idempotency_key: quoteKeys.current.get(signature)!, selection,
            session_key: selection === "one_session" ? sessionKey : null, cause: "learner_request" }])); } catch { /* rendered below */ }
        }}>Review cancellation</Button> : <Stack spacing={1.25}>
          <Alert severity="warning"><strong>{quote.sessions.length} {quote.sessions.length === 1 ? "session" : "sessions"}</strong> will be cancelled. Refund: <strong>{formatMoney(quote.policy_refund_amount, quote.currency)}</strong>.</Alert>
          {quote.sessions.map((session) => <Typography key={session.session_key} variant="body2">{coachingTime(session.starts_at, lesson.schedule.timezone)} · {formatMoney(session.policy_refund_amount, quote.currency)} refund</Typography>)}
          {coachingCancellationSummary(quote.accepted_cancellation_terms).map((line) => <Typography key={line} variant="caption" color="text.secondary">{line}</Typography>)}
          <Stack direction="row" spacing={1}>
            {quoteExpired ? <Alert severity="info">This quote expired. Refresh it to use the current policy and notice window.</Alert> : null}
            <Button color="error" variant="contained" disabled={busy || quoteExpired || !canAct()} onClick={async () => {
              if (!canAct()) return;
              if (!confirmKeys.current.has(quote.quote_id)) confirmKeys.current.set(quote.quote_id, crypto.randomUUID());
              try { await confirm.mutateAsync([acceptanceId, { quote_id: quote.quote_id, idempotency_key: confirmKeys.current.get(quote.quote_id)! }]); setHistoryRequested(true); discardQuote(); }
              catch { /* rendered below */ }
            }}>Confirm cancellation</Button>
            <Button disabled={busy} onClick={discardQuote}>{quoteExpired ? "Refresh quote" : "Keep sessions"}</Button>
          </Stack>
        </Stack>}
      </> : <Typography variant="body2" color="text.secondary">There are no future scheduled sessions to cancel.</Typography>}
      {createQuote.isError || confirm.isError ? <Alert severity="error" action={quote ? <Button size="small" onClick={discardQuote}>Refresh quote</Button> : undefined}>The lesson or refund terms changed. Refresh and request a new cancellation quote.</Alert> : null}
      {recover.isError ? <Alert severity="error">Refund recovery is unavailable. Refresh the status or contact support.</Alert> : null}
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography component="h4" variant="subtitle2">Cancellation history</Typography>
        <Button size="small" disabled={historyRequested && history.fetchStatus !== "idle"}
          onClick={() => { setHistoryRequested(true); void history.refetch(); }}>Refresh status</Button>
      </Stack>
      {history.isError ? <Alert severity="error">Cancellation status is unavailable. Refresh to try again.</Alert> : null}
      {history.data?.map((item) => <Alert key={item.command_id} severity={item.refund_status === "needs_reconciliation" ? "warning" : "info"}
        action={item.can_recover && (item.refund_status === "queued" || item.refund_status === "pending") ? <Button size="small" disabled={busy} onClick={() => recover.mutate([acceptanceId, item.command_id])}>Retry</Button> : undefined}>
        Cancellation refund: {item.refund_status.replaceAll("_", " ")} · {formatMoney(item.policy_refund_amount, item.currency)}
      </Alert>)}
    </Stack>
  </Paper>;
}
